/** @jsx isolateComponent */

import {
  Button,
  ColorName,
  Colors,
  Img,
  Link,
  Spacer,
  Surface,
  Text,
  Typography,
} from '@zapier/design-system';
import { isolateComponent } from '@zapier/style-encapsulation';

type CallToActionProps = {
  /** Header Image URL */
  ctaHeaderImage: string;
  /** Header Image Alt Text */
  ctaHeaderImageAlt: string;
  /** Main call to action text */
  ctaTitleText: string;
  /** Detail text under main cta */
  ctaDetailText: string;
  /** Main Call to Action button text */
  ctaButtonText: string;
  /** The URL that the button will navigate to */
  ctaButtonClickUrl: string;
  /** Called when the user clicks the CTA button */
  onClickCtaButton?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Called when the user presses a key while the CTA button is focused */
  onKeyDownCtaButton?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  /** Called when the user clicks the bottom link */
  onClickBottomLink?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Called when the user presses a key while the bottom link is focused */
  onKeyDownBottomLink?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  /** Learn more text */
  bottomLinkText: string;
  /** The URL that the bottom link text will navigate to */
  bottomLinkUrl: string;
  /** Background Color */
  backgroundColor?: ColorName;
};

const Styles = {
  bottomLinkSpan: {
    margin: '30px auto',
    display: 'flex',
    'align-items': 'center',
    'text-align': 'center',
  },
  bottomLink: {
    ...Typography.paragraph3Medium,
    margin: '0px auto',
    color: Colors.blueJeans,
    display: 'flex',
    'align-items': 'center',
    'text-align': 'center',
    'text-decoration-line': 'underline',
  },
  ctaBlock: {
    display: 'flex',
    margin: '0px auto',
    'padding-bottom': '40px',
    'padding-top': '35px',
    'flex-direction': 'column',
    'justify-content': 'center',
    'align-items': 'center',
    'text-align': 'center',
    'max-width': '525px',
  },
};

/**
  Generic version of the CTA created initially for the "Upgrade for better support" CTA
*/
const CallToActionBlock = ({
  onClickCtaButton,
  onKeyDownCtaButton,
  onClickBottomLink,
  onKeyDownBottomLink,
  backgroundColor,
  ctaTitleText,
  ctaDetailText,
  ctaButtonText,
  ctaHeaderImage,
  ctaHeaderImageAlt,
  bottomLinkText,
  ctaButtonClickUrl,
  bottomLinkUrl,
}: CallToActionProps) => (
  <div>
    <Surface backgroundColor={backgroundColor || 'neutral100'}>
      <Img alt={ctaHeaderImageAlt} isFullWidth={true} src={ctaHeaderImage} />
      <div style={Styles.ctaBlock}>
        <Text color="neutral800" tag="h3" textAlign="center" type="pageHeader7">
          {ctaTitleText}
        </Text>
        <Spacer height={20} />
        <Text color="neutral700" tag="p" textAlign="center" type="paragraph3">
          {ctaDetailText}
        </Text>
        <Spacer height={35} />
        <Button
          color="primary"
          href={ctaButtonClickUrl}
          onClick={onClickCtaButton}
          onKeyDown={onKeyDownCtaButton}
        >
          {ctaButtonText}
        </Button>
      </div>
    </Surface>
    <span style={Styles.bottomLinkSpan}>
      <Link
        href={bottomLinkUrl}
        onClick={onClickBottomLink}
        onKeyDown={onKeyDownBottomLink}
        style={Styles.bottomLink}
        target="_blank"
      >
        {bottomLinkText}
      </Link>
    </span>
  </div>
);

export default CallToActionBlock;
